/**
 * SSOCallbackHandler
 *
 * Pre-router component that intercepts SSO callback tokens.
 * Renders inside LoginProvider but BEFORE the HashRouter,
 * since ?loginToken=xxx lands in window.location.search (invisible to HashRouter).
 *
 * Flow:
 * 1. Check window.__pendingSSOToken / __pendingSSOError (set by index.jsx)
 * 2. If error → store in sessionStorage, redirect to #/login
 * 3. If token → validate CSRF, exchange for Matrix credentials, call ssoLogin()
 * 4. If neither → render null (no-op)
 */

import { useEffect, useState, useRef } from 'react';
import { useLogin } from '../../context/LoginContext';
import { useI18n } from '../../context/I18nContext';
import { validateAndConsumeCSRFState, exchangeLoginToken } from '../../services/ssoService';
import * as credentialStore from '../../services/credentialStore';
import logger from '../../utils/logger';

const SSOCallbackHandler = () => {
  const { ssoLogin } = useLogin();
  const { t } = useI18n();
  const [status, setStatus] = useState('idle'); // idle | processing | error
  const processedRef = useRef(false);

  useEffect(() => {
    // Only process once
    if (processedRef.current) return;

    const pendingToken = window.__pendingSSOToken;
    const pendingError = window.__pendingSSOError;
    const pendingNonce = window.__pendingSSONonce;

    // Nothing to process
    if (!pendingToken && !pendingError) return;

    processedRef.current = true;

    // Handle SSO error from Keycloak
    if (pendingError) {
      logger.warn('SSOCallbackHandler: SSO error received:', pendingError);
      window.__pendingSSOError = null;
      window.__pendingSSONonce = null;
      try {
        sessionStorage.setItem('sso:error', pendingError);
      } catch {
        // sessionStorage unavailable
      }
      window.dispatchEvent(new Event('sso:callback-complete'));
      window.location.hash = '#/login';
      return;
    }

    // Handle SSO token
    if (pendingToken) {
      // Consume-and-delete immediately (prevents CDN script access)
      const token = pendingToken;
      window.__pendingSSOToken = null;
      window.__pendingSSONonce = null;

      // Guard: already authenticated — discard token (prevents identity switching)
      if (credentialStore.hasCredentials()) {
        logger.warn('SSOCallbackHandler: Already authenticated, discarding SSO token');
        window.dispatchEvent(new Event('sso:callback-complete'));
        return;
      }

      // Guard: CSRF nonce must exist (proves SSO was initiated from this app)
      let csrfStateExists = false;
      try {
        csrfStateExists = !!sessionStorage.getItem('sso:csrf_state');
      } catch {
        // sessionStorage unavailable
      }

      if (!csrfStateExists) {
        logger.warn('SSOCallbackHandler: No CSRF state found — SSO not initiated from this app');
        try {
          sessionStorage.setItem('sso:error', t('LoginPage.ssoSecurityFailed'));
        } catch {
          // sessionStorage unavailable
        }
        window.dispatchEvent(new Event('sso:callback-complete'));
        window.location.hash = '#/login';
        return;
      }

      // Validate CSRF nonce and recover homeserver
      const csrfResult = validateAndConsumeCSRFState(pendingNonce);
      if (!csrfResult.valid) {
        logger.warn('SSOCallbackHandler: CSRF nonce validation failed');
        try {
          sessionStorage.setItem('sso:error', t('LoginPage.ssoSecurityFailed'));
        } catch {
          // sessionStorage unavailable
        }
        window.dispatchEvent(new Event('sso:callback-complete'));
        window.location.hash = '#/login';
        return;
      }

      const ssoHomeserver = csrfResult.homeserver;

      // Process token exchange
      setStatus('processing');
      processToken(token, ssoHomeserver);
    }

    async function processToken(loginToken, homeserver) {
      try {
        // Exchange loginToken for Matrix credentials
        const loginResponse = await exchangeLoginToken(loginToken, homeserver);

        // Validate loginResponse structure
        if (!loginResponse?.access_token || !loginResponse?.device_id || !loginResponse?.user_id) {
          throw new Error('Invalid SSO login response from server');
        }

        // Call ssoLogin on LoginContext with dynamic homeserver
        const result = await ssoLogin(loginResponse, homeserver);

        if (!result.success) {
          throw new Error(result.error || 'SSO login failed');
        }

        // Clear the processing overlay before navigating
        setStatus('idle');
        window.dispatchEvent(new Event('sso:callback-complete'));

        // Navigate to conversations (outside Router, use hash directly)
        window.location.hash = '#/conversations';

      } catch (error) {
        logger.error('SSOCallbackHandler: Token exchange failed:', error);
        setStatus('error');
        try {
          sessionStorage.setItem('sso:error', error.message || t('LoginPage.ssoLoginFailed'));
        } catch {
          // sessionStorage unavailable
        }
        window.dispatchEvent(new Event('sso:callback-complete'));
        window.location.hash = '#/login';
      }
    }
  }, [ssoLogin, t]);

  // Show loading overlay during token exchange
  if (status === 'processing') {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#fff',
        zIndex: 9999,
        fontSize: '1.2rem',
        color: '#666',
      }}>
        <div style={{
          width: 40,
          height: 40,
          border: '3px solid #e0e0e0',
          borderTop: '3px solid #0066cc',
          borderRadius: '50%',
          animation: 'sso-spin 1s linear infinite',
          marginBottom: 16,
        }} />
        <span>{t('LoginPage.signingIn')}</span>
        <style>{`
          @keyframes sso-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Render nothing when idle or on error (error redirects to login)
  return null;
};

export default SSOCallbackHandler;
